export const tools = [
    {
        name: 'billing_Subscriptions_list',
        description: 'List tenant subscriptions, optionally by status.',
        context: 'billing',
        operationId: 'Subscriptions_list',
        method: 'GET',
        path: '/admin/subscriptions',
        action: 'subscription:list',
        auth: 'platform-admin',
        inputSchema: {
            type: 'object',
            properties: {
                query: {
                    type: 'object',
                    properties: {
                        cursor: {
                            type: 'string',
                        },
                        limit: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 200,
                            default: 50,
                        },
                        status: {
                            type: 'string',
                            enum: [
                                'none',
                                'incomplete',
                                'trialing',
                                'active',
                                'past_due',
                                'unpaid',
                                'cancelled',
                                'paused',
                            ],
                            description: 'Subscription lifecycle as billing tracks it (Stripe statuses, plus `none` before checkout).',
                        },
                    },
                    required: [],
                    additionalProperties: false,
                },
            },
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'billing_Subscriptions_get',
        description: "One tenant's subscription record.",
        context: 'billing',
        operationId: 'Subscriptions_get',
        method: 'GET',
        path: '/admin/subscriptions/{tenantId}',
        action: 'subscription:read',
        auth: 'platform-admin',
        inputSchema: {
            type: 'object',
            properties: {
                params: {
                    type: 'object',
                    properties: {
                        tenantId: {
                            type: 'string',
                            pattern: '^[a-z0-9][a-z0-9-]{1,62}$',
                            description: "Tenant identifier. The control plane's own resources use the literal `platform`.",
                        },
                    },
                    required: ['tenantId'],
                    additionalProperties: false,
                },
            },
            required: ['params'],
            additionalProperties: false,
        },
    },
    {
        name: 'billing_Subscriptions_adjust',
        description: "Adjust a tenant's subscription on Stripe: seat quantity, cancel at period end. Audited.",
        context: 'billing',
        operationId: 'Subscriptions_adjust',
        method: 'POST',
        path: '/admin/subscriptions/{tenantId}/adjust',
        action: 'subscription:adjust',
        auth: 'platform-admin',
        inputSchema: {
            type: 'object',
            properties: {
                params: {
                    type: 'object',
                    properties: {
                        tenantId: {
                            type: 'string',
                            pattern: '^[a-z0-9][a-z0-9-]{1,62}$',
                            description: "Tenant identifier. The control plane's own resources use the literal `platform`.",
                        },
                    },
                    required: ['tenantId'],
                    additionalProperties: false,
                },
                body: {
                    type: 'object',
                    required: ['note'],
                    properties: {
                        seats: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 1000,
                            description: 'Set the seat quantity on Stripe (per-seat packages only).',
                        },
                        cancelAtPeriodEnd: {
                            type: 'boolean',
                            description: 'Cancel at period end (true) or undo a pending cancellation (false).',
                        },
                        note: {
                            type: 'string',
                            minLength: 3,
                            maxLength: 500,
                            description: "Why. Recorded on the subscription's audit trail and as Stripe metadata.",
                        },
                    },
                    description: 'An operator adjustment. Every field is optional but the note; the change is audited.',
                },
            },
            required: ['params', 'body'],
            additionalProperties: false,
        },
    },
    {
        name: 'billing_Checkouts_create',
        description: "Create (or resume) a hosted Stripe Checkout Session for the tenant's package. Returns the URL to redirect to.",
        context: 'billing',
        operationId: 'Checkouts_create',
        method: 'POST',
        path: '/checkout',
        action: 'checkout:create',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                body: {
                    type: 'object',
                    properties: {
                        packageId: {
                            type: 'string',
                            pattern: '^pkg_[a-z0-9-]+$',
                            description: 'Package identifier (`pkg_...`), as the platform catalog defines it.',
                        },
                        seats: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 1000,
                            description: 'Seat quantity for per-seat packages; ignored for flat. Defaults to the counted memberships (min 1).',
                        },
                        successPath: {
                            type: 'string',
                            maxLength: 512,
                            pattern: "^/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$",
                            description: "Path the tenant returns to after paying (host is always the tenant's cell).",
                        },
                        cancelPath: {
                            type: 'string',
                            maxLength: 512,
                            pattern: "^/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$",
                            description: 'Path the tenant returns to after cancelling.',
                        },
                    },
                    description: "Start (or resume) Checkout for the caller's tenant. The package and price come from the\nplatform (`platform.tenant.checkoutRequested`); `packageId`, when given, must match it —\na different package is adopted through the platform first, never here.",
                },
            },
            required: ['body'],
            additionalProperties: false,
        },
    },
    {
        name: 'billing_Portals_create',
        description: 'Open a Stripe Customer Portal session for the tenant (payment method, invoices, cancellation).',
        context: 'billing',
        operationId: 'Portals_create',
        method: 'POST',
        path: '/portal',
        action: 'portal:create',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                body: {
                    type: 'object',
                    properties: {
                        returnPath: {
                            type: 'string',
                            maxLength: 512,
                            pattern: "^/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$",
                            description: "Path the tenant returns to from the portal (host is always the tenant's cell).",
                        },
                    },
                },
            },
            required: ['body'],
            additionalProperties: false,
        },
    },
    {
        name: 'billing_Summary_get',
        description: "The caller's tenant billing state: plan, seats, current period, next and last invoice, portal availability.",
        context: 'billing',
        operationId: 'Summary_get',
        method: 'GET',
        path: '/summary',
        action: 'billing:read',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {},
            required: [],
            additionalProperties: false,
        },
    },
];
//# sourceMappingURL=tools.js.map